import IconButton from '@/components/Buttons/IconButton';
import { GET_CURRENT_SUBSCRIPTION_KEY } from '@/pages/video-library/constants';
import { useAnalytics } from '@/services/analytics';
import { StyleCollection } from '@/utils/types';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import AccountNotPrimaryBanner from './AccountNotPrimaryBanner';
import AgentsBanner from './AgentsBanner';
import { CreditsFinishingBanner } from './CreditsFinishingBanner';
import { AppBannerType, useAppBanner } from './hooks';
import MarketingBanner from './MarketingBanner';
import PaymentUnsuccessfulBanner from './PaymentUnsuccessfulBanner';

export const BANNER_HEIGHT = '60px';

const Container = styled.div<{ isWarning: boolean }>`
    display: flex;
    position: relative;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: ${BANNER_HEIGHT};
    background: ${({ theme, isWarning }) => (isWarning ? theme.colors.error[50] : theme.colors.primary.main)};
    z-index: 1;
    padding: 16px 0px;
`;

const CloseWrapper = styled.div<{ show: boolean }>`
    display: ${({ show }) => (show ? 'flex' : 'none')};
    position: absolute;
    right: 2rem;
`;

const styles: StyleCollection = {
    closeIcon: theme => ({
        color: theme.colors.secondary,
        fontSize: '2.5rem',
    }),
    cta: theme => ({
        marginLeft: '4rem',
        color: theme.colors.primary.main,
        background: `${theme.colors.secondary} !important`,
    }),
};

const getBanner = (type: AppBannerType) => {
    switch (type) {
        case AppBannerType.Marketing:
            return <MarketingBanner />;
        case AppBannerType.CreditsFinishing:
            return <CreditsFinishingBanner />;
        case AppBannerType.AccountNotPrimary:
            return <AccountNotPrimaryBanner />;
        case AppBannerType.Agents:
            return <AgentsBanner />;
        case AppBannerType.PaymentUnsuccessfulBanner:
            return <PaymentUnsuccessfulBanner />;
        default:
            return null;
    }
};

export const AppBanner = () => {
    const mixpanel = useAnalytics();
    const { type, close } = useAppBanner();
    const theme = useTheme();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (type) {
            mixpanel?.track('app-banner', { type, event: 'view' });
        }
    }, []);

    const isPaymentUnsuccessfulBanner = type === AppBannerType.PaymentUnsuccessfulBanner;

    const onClosed = () => {
        mixpanel?.track('app-banner', { type, event: 'close' });
        if (isPaymentUnsuccessfulBanner) {
            queryClient.setQueryData(GET_CURRENT_SUBSCRIPTION_KEY, { subscription: undefined });
        }
        close();
    };

    return (
        <Container isWarning={isPaymentUnsuccessfulBanner}>
            {getBanner(type)}
            <CloseWrapper show>
                <IconButton ariaLabel="close" onClick={onClosed}>
                    <CloseIcon sx={styles.closeIcon} htmlColor={theme.colors.secondary.main} />
                </IconButton>
            </CloseWrapper>
        </Container>
    );
};
